export type AuthenticationType = "certificate" | "jwt" | "symmetric_key";
export type KeyType = "rsa" | "ecc" | "aes";
export type HashType = "sha256" | "sha384" | "sha512";

export interface CertificatePolicy {
  id: string;
  name: string;
  authenticationType: AuthenticationType;
  durationDays: number;
  autoRenew: boolean;
  keyType: KeyType;
  keyLength: number;
  hashType: HashType;
  signatureAlgorithm: string;
  createdAt: string;
  isDefault: boolean;
}

export const AUTHENTICATION_TYPES: AuthenticationType[] = [
  "certificate",
  "jwt",
  "symmetric_key",
];
export const KEY_TYPES: KeyType[] = ["rsa", "ecc", "aes"];
export const HASH_TYPES: HashType[] = ["sha256", "sha384", "sha512"];

export function isAuthenticationType(
  value: string
): value is AuthenticationType {
  return AUTHENTICATION_TYPES.includes(value as AuthenticationType);
}

export function isKeyType(value: string): value is KeyType {
  return KEY_TYPES.includes(value as KeyType);
}

export function isHashType(value: string): value is HashType {
  return HASH_TYPES.includes(value as HashType);
}

export const KEY_LENGTHS_BY_TYPE: Record<KeyType, number[]> = {
  rsa: [2048, 4096],
  ecc: [256, 384, 512],
  aes: [128, 192, 256],
};

export function getSignatureAlgorithm(
  keyType: KeyType,
  hashType: HashType
): string {
  const hash = hashType.toUpperCase();
  if (keyType === "rsa") return `${hash}withRSA`;
  if (keyType === "ecc") return `${hash}withECDSA`;
  return hash;
}

/** Fields that define the identity of a policy, excluding id/createdAt/isDefault. */
export function certificatePolicyFingerprint(
  p: Pick<
    CertificatePolicy,
    | "name"
    | "authenticationType"
    | "durationDays"
    | "autoRenew"
    | "keyType"
    | "keyLength"
    | "hashType"
    | "signatureAlgorithm"
  >
): string {
  return JSON.stringify([
    p.name,
    p.authenticationType,
    p.durationDays,
    p.autoRenew,
    p.keyType,
    p.keyLength,
    p.hashType,
    p.signatureAlgorithm,
  ]);
}
